import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../auth/useAuth";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import {
  createEmergencyAccessClient,
  type EmergencyAccessConfig,
  type EmergencyAccessPatch,
  type EmergencyCard,
} from "./client";

export type EmergencyAccessStatus = "idle" | "loading" | "ready" | "saving" | "error";

export function useEmergencyAccess() {
  const auth = useAuth();
  const config = useRuntimeConfig();
  const apiBaseUrl = config?.accountService?.apiBaseUrl;
  const [emergencyAccess, setEmergencyAccess] = useState<EmergencyAccessConfig | null>(null);
  const [deviceEmergencyAccessEnabled, setDeviceEmergencyAccessEnabled] = useState(false);
  const [status, setStatus] = useState<EmergencyAccessStatus>("idle");
  const [deviceStatus, setDeviceStatus] = useState<EmergencyAccessStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  const client = useMemo(() => {
    if (!apiBaseUrl) {
      return null;
    }
    return createEmergencyAccessClient(apiBaseUrl, () => auth.user?.access_token);
  }, [apiBaseUrl, auth.user]);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!client) {
        setError("Emergency Access is not available yet.");
        setStatus("error");
        return;
      }

      setStatus("loading");
      setError(null);

      try {
        const next = await client.getConfig(signal);
        if (signal?.aborted) {
          return;
        }
        setEmergencyAccess(next);
        const deviceEnabled = await client.getDeviceStatus(signal);
        if (signal?.aborted) {
          return;
        }
        setDeviceEmergencyAccessEnabled(deviceEnabled);
        setStatus("ready");
        setDeviceStatus("ready");
      } catch (nextError) {
        if (nextError instanceof DOMException && nextError.name === "AbortError") {
          return;
        }
        setError(nextError instanceof Error ? nextError.message : "We could not load Emergency Access.");
        setStatus("error");
      }
    },
    [client],
  );

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return undefined;
    }
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [auth.isAuthenticated, load]);

  const saveSelections = useCallback(
    async (patch: EmergencyAccessPatch) => {
      if (!client) {
        setError("Emergency Access is not available yet.");
        return null;
      }
      setStatus("saving");
      setError(null);
      try {
        const next = await client.updateSelections(patch);
        setEmergencyAccess(next);
        setStatus("ready");
        return next;
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : "We could not save your Emergency Access selections.");
        setStatus("ready");
        return null;
      }
    },
    [client],
  );

  const enable = useCallback(async () => {
    if (!client) {
      setError("Emergency Access is not available yet.");
      return null;
    }
    setStatus("saving");
    setError(null);
    try {
      const result = await client.enable();
      setEmergencyAccess(result);
      setDeviceStatus("saving");
      setDeviceError(null);
      try {
        const deviceEnabled = await client.enableCurrentDevice();
        if (!deviceEnabled) {
          throw new Error("device_not_enabled");
        }
        setDeviceEmergencyAccessEnabled(deviceEnabled);
      } catch {
        setDeviceEmergencyAccessEnabled(false);
        setDeviceError("Emergency Access is enabled, but this device could not be enabled. You can retry below.");
      }
      setDeviceStatus("ready");
      setStatus("ready");
      return result;
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "We could not enable Emergency Access.");
      setStatus("ready");
      return null;
    }
  }, [client]);

  const disable = useCallback(async () => {
    if (!client) {
      setError("Emergency Access is not available yet.");
      return false;
    }
    setStatus("saving");
    setError(null);
    try {
      const next = await client.disable();
      setEmergencyAccess(next);
      setDeviceEmergencyAccessEnabled(false);
      setStatus("ready");
      return true;
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "We could not disable Emergency Access.");
      setStatus("ready");
      return false;
    }
  }, [client]);

  const enableCurrentDevice = useCallback(async () => {
    if (!client) {
      setDeviceError("Emergency Access is not available yet.");
      return false;
    }
    setDeviceStatus("saving");
    setDeviceError(null);
    try {
      const enabled = await client.enableCurrentDevice();
      setDeviceEmergencyAccessEnabled(enabled);
      setDeviceStatus("ready");
      return enabled;
    } catch (nextError) {
      setDeviceError(nextError instanceof Error ? nextError.message : "We could not allow Emergency Access on this device.");
      setDeviceStatus("ready");
      return false;
    }
  }, [client]);

  const disableCurrentDevice = useCallback(async () => {
    if (!client) {
      setDeviceError("Emergency Access is not available yet.");
      return false;
    }
    setDeviceStatus("saving");
    setDeviceError(null);
    try {
      await client.disableCurrentDevice();
      setDeviceEmergencyAccessEnabled(false);
      setDeviceStatus("ready");
      return true;
    } catch (nextError) {
      setDeviceError(nextError instanceof Error ? nextError.message : "We could not forget Emergency Access on this device.");
      setDeviceStatus("ready");
      return false;
    }
  }, [client]);

  const preview = useCallback(async (): Promise<EmergencyCard | null> => {
    if (!client) {
      setError("Emergency Access is not available yet.");
      return null;
    }
    try {
      return await client.preview();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "We could not load the Emergency Card preview.");
      return null;
    }
  }, [client]);

  return {
    deviceEmergencyAccessEnabled,
    deviceError,
    deviceStatus,
    disable,
    disableCurrentDevice,
    emergencyAccess,
    enable,
    enableCurrentDevice,
    error,
    load,
    preview,
    saveSelections,
    status,
  };
}
